import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import type { PendingContribution } from '../types';

interface PendingBatchListProps {
  contributions: PendingContribution[];
  onRemove: (id: string) => void;
  onEdit: (id: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const PendingBatchList: React.FC<PendingBatchListProps> = ({
  contributions,
  onRemove,
  onEdit,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const totalAmount = contributions.reduce((sum, c) => sum + c.amount, 0);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (contributions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-md p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              Pending Contributions ({contributions.length})
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Review all contributions before submitting for approval
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-foreground">
              {totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })} VT
            </div>
            <div className="text-xs text-muted-foreground">Total Amount</div>
          </div>
        </div>

        <div className="space-y-2 max-h-96 overflow-y-auto">
          {contributions.map((contrib) => (
            <div
              key={contrib.id}
              className="flex items-start gap-3 p-3 bg-muted/30 rounded-md border border-border hover:bg-muted/50 transition-colors"
            >
              <Image
                src={contrib.memberAvatar}
                alt={contrib.memberName}
                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
              />
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-foreground truncate">
                      {contrib.memberName}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(contrib.date)}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-base font-semibold text-foreground">
                      {contrib.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })} VT
                    </div>
                  </div>
                </div>

                {contrib.notes && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                    <Icon name="FileText" size={12} />
                    <span className="truncate max-w-[200px]">{contrib.notes}</span>
                  </div>
                )}

                <div className="flex items-center gap-2 mt-2">
                  <button
                    onClick={() => onEdit(contrib.id)}
                    className="text-xs text-primary hover:text-primary/80 transition-interactive font-medium flex items-center gap-1"
                  >
                    <Icon name="Edit" size={12} />
                    Edit
                  </button>
                  <button
                    onClick={() => onRemove(contrib.id)}
                    className="text-xs text-destructive hover:text-destructive/80 transition-interactive font-medium flex items-center gap-1"
                  >
                    <Icon name="Trash2" size={12} />
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-3 pt-4 mt-4 border-t border-border">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="flex-1"
            onClick={onSubmit}
            disabled={isSubmitting || contributions.length === 0}
            iconName={isSubmitting ? 'Loader2' : 'Check'}
            iconPosition="left"
          >
            {isSubmitting ? 'Submitting...' : `Submit ${contributions.length} Contribution${contributions.length !== 1 ? 's' : ''} for Approval`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PendingBatchList;
